import React, { useEffect, useMemo, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Switch, TextField } from "@mui/material";
import styles from "./styles.module.scss";
import { useParams } from "react-router-dom";
import { PublicContractedMcpDTO, PublicMcpDTO, ToolENV, McpLibraryServices, NotistackServices } from "shared/services";
import { HandleError, navigate } from "shared/utils";

interface ToolManagementContentProps {
	contractedMcps: PublicContractedMcpDTO[];
	loadingList?: boolean;
	onUpdate?: () => void;
	onHired?: (contract: PublicContractedMcpDTO) => void;
	onContractUpdated?: (contract: PublicContractedMcpDTO) => void;
	onContractRemoved?: (id: string) => void;
}

/** Gera iniciais a partir do nome (até 2 letras). */
function getInitials(name: string): string {
	return name
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

function buildConfigPayload(fields: Array<{ envName: string; type: string }>, values: Record<string, any>): Record<string, any> {
	const payload: Record<string, any> = {};

	for (const field of fields) {
		if (field.type.startsWith("OAuth2:")) {
			continue;
		}

		const rawValue = values[field.envName];
		if (rawValue === undefined) continue;

		if (field.type === "number" && typeof rawValue === "string") {
			const trimmed = rawValue.trim();
			payload[field.envName] = trimmed === "" ? rawValue : Number(trimmed);
			continue;
		}

		if (field.type === "json" && typeof rawValue === "string") {
			const trimmed = rawValue.trim();
			if (trimmed === "") {
				payload[field.envName] = rawValue;
				continue;
			}
			try {
				payload[field.envName] = JSON.parse(trimmed);
			} catch {
				payload[field.envName] = rawValue;
			}
			continue;
		}

		payload[field.envName] = rawValue;
	}

	return payload;
}

interface OAuthConnectionState {
	linked: boolean;
	provider: string;
	linkedAt?: string;
}

function getOAuthProviderFromType(type: string): string {
	if (!type.startsWith("OAuth2:")) return "oauth2";
	const provider = type.slice("OAuth2:".length).trim();
	return provider || "oauth2";
}

function getOAuthConnectionState(type: string, value: any): OAuthConnectionState {
	const provider = getOAuthProviderFromType(type);

	if (value && typeof value === "object") {
		const linked =
			Boolean(value.linked) ||
			(typeof value.access_token === "string" && value.access_token.trim() !== "") ||
			(typeof value.refresh_token === "string" && value.refresh_token.trim() !== "") ||
			(value.token && typeof value.token === "object");

		const linkedAt = typeof value.linkedAt === "string" ? value.linkedAt : typeof value.linked_at === "string" ? value.linked_at : undefined;

		return {
			linked,
			provider,
			linkedAt,
		};
	}

	if (typeof value === "string") {
		return {
			linked: value.trim().length > 0,
			provider,
		};
	}

	return {
		linked: false,
		provider,
	};
}

function normalizeConfigValue(value: any): any {
	if (Array.isArray(value)) {
		return value.map((item) => normalizeConfigValue(item));
	}

	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce(
				(acc, key) => {
					acc[key] = normalizeConfigValue(value[key]);
					return acc;
				},
				{} as Record<string, any>,
			);
	}

	return value;
}

function areConfigsEqual(a: Record<string, any>, b: Record<string, any>): boolean {
	return JSON.stringify(normalizeConfigValue(a)) === JSON.stringify(normalizeConfigValue(b));
}

const EMPTY_CONFIG: Record<string, any> = {};
const EMPTY_TOOL_ENV_FIELDS: ToolENV[] = [];

export const ToolManagementContent: React.FC<ToolManagementContentProps> = ({ contractedMcps, loadingList = false, onUpdate, onHired, onContractUpdated, onContractRemoved }) => {
	const { toolId, mcpId } = useParams<{ toolId: string; mcpId: string }>();

	const selectedContract = useMemo(() => {
		if (!toolId) return null;
		return contractedMcps.find((contract) => String(contract.id) === toolId) ?? null;
	}, [toolId, contractedMcps]);

	const [selectedMcp, setSelectedMcp] = useState<PublicMcpDTO | null>(selectedContract?.mcp ?? null);
	const [loadingMcp, setLoadingMcp] = useState(false);

	useEffect(() => {
		const targetMcpId = selectedContract ? String(selectedContract.mcpId) : mcpId;

		if (!targetMcpId) {
			setSelectedMcp(null);
			setLoadingMcp(false);
			return;
		}

		if (selectedContract?.mcp && String(selectedContract.mcp.id) === targetMcpId) {
			setSelectedMcp(selectedContract.mcp);
			setLoadingMcp(false);
			return;
		}

		let cancelled = false;
		setLoadingMcp(true);

		McpLibraryServices.listMcps()
			.then((mcps) => {
				if (cancelled) return;
				const mcp = mcps.find((item) => String(item.id) === targetMcpId) ?? null;
				setSelectedMcp(mcp);
			})
			.catch((error) => {
				if (cancelled) return;
				setSelectedMcp(null);
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar detalhes do MCP");
			})
			.finally(() => {
				if (!cancelled) setLoadingMcp(false);
			});

		return () => {
			cancelled = true;
		};
	}, [selectedContract, mcpId]);

	const mcp = selectedMcp;
	const toolEnvFields = mcp?.toolEnvFields ?? EMPTY_TOOL_ENV_FIELDS;
	const contractedConfig = useMemo(() => selectedContract?.config ?? EMPTY_CONFIG, [selectedContract]);

	const [toolEnvValues, setToolEnvValues] = useState<Record<string, any>>({});
	const [saving, setSaving] = useState(false);
	const [hiring, setHiring] = useState(false);
	const [oauthLoadingByEnv, setOauthLoadingByEnv] = useState<Record<string, "link" | "unlink" | undefined>>({});
	const [togglingActive, setTogglingActive] = useState(false);
	const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
	const [removing, setRemoving] = useState(false);

	const originalConfigPayload = useMemo(() => buildConfigPayload(toolEnvFields, contractedConfig), [toolEnvFields, contractedConfig]);
	const currentConfigPayload = useMemo(() => buildConfigPayload(toolEnvFields, toolEnvValues), [toolEnvFields, toolEnvValues]);

	const hasConfigChanges = useMemo(() => {
		if (!selectedContract) return false;
		return !areConfigsEqual(originalConfigPayload, currentConfigPayload);
	}, [selectedContract, originalConfigPayload, currentConfigPayload]);

	useEffect(() => {
		if (!selectedContract) {
			setToolEnvValues({});
			return;
		}
		setToolEnvValues({ ...contractedConfig });
	}, [selectedContract, contractedConfig]);

	const handleToolEnvChange = (envName: string, value: any) => {
		setToolEnvValues((prev) => ({ ...prev, [envName]: value }));
	};

	const handleSave = async () => {
		if (!selectedContract || !hasConfigChanges) return;
		setSaving(true);
		try {
			await McpLibraryServices.configureHiredMcp(String(selectedContract.id), {
				config: currentConfigPayload,
			});
			NotistackServices.showSuccessMessage("Configurações salvas com sucesso");
			onUpdate?.();
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao salvar configurações");
		} finally {
			setSaving(false);
		}
	};

	const handleHire = async () => {
		if (!mcp) return;

		setHiring(true);
		try {
			const configPayload = buildConfigPayload(toolEnvFields, toolEnvValues);
			const contract = await McpLibraryServices.hireMcp({
				mcp_id: String(mcp.id),
				...(Object.keys(configPayload).length > 0 ? { config: configPayload } : {}),
			});

			NotistackServices.showSuccessMessage("MCP contratado com sucesso");
			onHired?.(contract);
			onUpdate?.();

			if (!onHired) {
				navigate(`/tools/${contract.id}`);
			}
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao contratar MCP");
		} finally {
			setHiring(false);
		}
	};

	const setOAuthLoading = (envName: string, action?: "link" | "unlink") => {
		setOauthLoadingByEnv((prev) => ({ ...prev, [envName]: action }));
	};

	const handleOAuth2Link = async (envName: string) => {
		setOAuthLoading(envName, "link");
		try {
			let contractId = selectedContract ? String(selectedContract.id) : null;

			// Se ainda não contratou, contrata agora antes de iniciar o OAuth.
			if (!contractId) {
				if (!mcp) throw new Error("MCP indisponível para contratação");
				const configPayload = buildConfigPayload(toolEnvFields, toolEnvValues);
				const contract = await McpLibraryServices.hireMcp({
					mcp_id: String(mcp.id),
					...(Object.keys(configPayload).length > 0 ? { config: configPayload } : {}),
				});
				contractId = String(contract.id);
				onHired?.(contract);
				onUpdate?.();
			}

			// callbackUrl precisa apontar para a tela do contrato (não da biblioteca)
			const { origin, pathname } = window.location;
			const callbackUrl = pathname.includes("/tools/mcp/") ? `${origin}/tools/${contractId}` : window.location.href;

			const response = await McpLibraryServices.initHiredMcpOAuth2(contractId, {
				envName,
				callbackUrl,
			});

			if (!response.redirectUrl) {
				throw new Error("URL de autorização OAuth2 não foi retornada");
			}

			window.location.assign(response.redirectUrl);
		} catch (error) {
			setOAuthLoading(envName, undefined);
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao iniciar vínculo OAuth2");
		}
	};

	const handleToggleActive = async () => {
		if (!selectedContract) return;
		const wasActive = selectedContract.isActive;
		setTogglingActive(true);
		try {
			const updated = await McpLibraryServices.setHiredMcpActive(String(selectedContract.id), !wasActive);
			onContractUpdated?.(updated);
			NotistackServices.showSuccessMessage(wasActive ? "Ferramenta desativada" : "Ferramenta ativada");
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao alterar status da ferramenta");
		} finally {
			setTogglingActive(false);
		}
	};

	const handleConfirmRemove = async () => {
		if (!selectedContract) return;
		const contractId = String(selectedContract.id);
		setRemoving(true);
		try {
			const result = await McpLibraryServices.removeHiredMcp(contractId);
			const memberMsg = result.unlinkedMembersCount > 0
				? ` ${result.unlinkedMembersCount} ${result.unlinkedMembersCount === 1 ? "especialista foi desvinculado" : "especialistas foram desvinculados"}.`
				: "";
			NotistackServices.showSuccessMessage(`Ferramenta removida com sucesso.${memberMsg}`);
			setRemoveDialogOpen(false);
			onContractRemoved?.(contractId);
			navigate("/tools");
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao remover ferramenta");
		} finally {
			setRemoving(false);
		}
	};

	const handleOAuth2Unlink = async (envName: string) => {
		if (!selectedContract) return;

		setOAuthLoading(envName, "unlink");
		try {
			const updated = await McpLibraryServices.unlinkHiredMcpOAuth2(String(selectedContract.id), { envName });
			// Update otimistico: usa o contrato atualizado retornado pela API. Antes
			// chamavamos `onUpdate()` que disparava um refetch com debounce de 1s no
			// parent — durante esse intervalo o botao continuava "Desconectar" porque
			// o `toolEnvValues` local ainda referenciava o slot OAuth antigo. Agora o
			// state da lista atualiza na hora e o botao vira "Vincular".
			onContractUpdated?.(updated);
			// Tambem zera o valor local do campo OAuth para refletir desvinculacao,
			// caso o useEffect que sincroniza `toolEnvValues` ainda nao tenha rodado.
			setToolEnvValues((prev) => {
				if (!(envName in prev)) return prev;
				const next = { ...prev };
				delete next[envName];
				return next;
			});
			NotistackServices.showSuccessMessage("Conta desconectada com sucesso");
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao desconectar conta");
		} finally {
			setOAuthLoading(envName, undefined);
		}
	};

	// Enquanto a lista de contratos ou os detalhes do MCP estiverem carregando, exibe
	// spinner. Sem o `loadingList`, a primeira renderização (antes do fetch da lista
	// completar) caía direto no "MCP não encontrado" quando o user abria a página por
	// URL direta tipo /tools/:contractId.
	if (!selectedContract && (loadingMcp || loadingList)) {
		return (
			<div className={styles.managementContainer}>
				<div className={styles.loadingContainer}>
					<CircularProgress size={24} />
				</div>
			</div>
		);
	}

	if (!mcp) {
		return (
			<div className={styles.managementContainer}>
				<div className={styles.loadingContainer}>
					<p>MCP não encontrado na biblioteca.</p>
					<Button
						variant="contained"
						color="primary"
						onClick={() => navigate("/tools")}
					>
						Voltar para biblioteca
					</Button>
				</div>
			</div>
		);
	}

	const statusInfo = !selectedContract
		? { label: "Não contratado", className: "statusPending" }
		: !selectedContract.isActive
			? { label: "Inativo", className: "statusInactive" }
			: { label: "Ativo", className: "statusActive" };

	const deliverableTypesLabel = mcp?.deliverableTypes?.length ? mcp.deliverableTypes.join(", ") : "—";

	return (
		<div className={styles.managementContainer}>
			{/* Header & Metrics */}
			<div className={styles.headerSection}>
				<div className={styles.headerLeft}>
					<div
						className={styles.toolLogo}
						style={{ backgroundColor: "var(--mui-palette-primary-main)" }}
					>
						<span>{getInitials(mcp?.name ?? "MCP")}</span>
					</div>
					<div>
						<h1 className={styles.toolName}>{mcp?.name ?? "MCP contratado"}</h1>
						<div className={styles.connectionStatus}>
							<span className={styles.statusDot} />
							<span>{statusInfo.label}</span>
							{selectedContract && (
								<>
									<Switch
										checked={selectedContract.isActive}
										onChange={handleToggleActive}
										disabled={togglingActive}
										color="primary"
										size="small"
									/>
									<span>
										{togglingActive ? "Atualizando..." : selectedContract.isActive ? "Desativar" : "Ativar"}
									</span>
								</>
							)}
						</div>
					</div>
				</div>
				<div className={styles.metricsRow}>
					<div className={styles.metricCard}>
						<p className={styles.metricLabel}>Contrato</p>
						<p className={styles.metricValue}>{selectedContract ? String(selectedContract.id).slice(0, 8) : "—"}</p>
					</div>
					<div className={styles.metricCard}>
						<p className={styles.metricLabel}>Slug</p>
						<p className={styles.metricValue}>{mcp?.slug ?? "—"}</p>
					</div>
				</div>
			</div>

			{/* Form */}
			<div className={styles.formBody}>
				{/* MCP info */}
				<div className={styles.formGrid}>
					<TextField
						className={styles.formInput}
						label="Nome do MCP"
						variant="outlined"
						size="small"
						fullWidth
						value={mcp?.name ?? "—"}
						InputProps={{ readOnly: true }}
					/>
				</div>

				<TextField
					className={styles.formTextarea}
					label="Descrição do MCP"
					variant="outlined"
					size="small"
					fullWidth
					multiline
					minRows={3}
					value={mcp?.description ?? "Sem descrição"}
					InputProps={{ readOnly: true }}
				/>

				{/* ENV Fields */}
				{toolEnvFields.length > 0 && (
					<div className={styles.envSection}>
						<div className={styles.envGrid}>
							{toolEnvFields.map((field) => {
								const currentValue = toolEnvValues[field.envName];
								const isOAuth2 = field.type.startsWith("OAuth2:");
								const oauthStatus = isOAuth2 ? getOAuthConnectionState(field.type, currentValue) : null;
								const oauthAction = oauthLoadingByEnv[field.envName];
								const oauthScopes = Array.isArray(field.scopes)
									? field.scopes
									: Array.isArray(field.oauthScopes)
										? field.oauthScopes
										: [];

								return (
									<div
										key={field.envName}
										className={styles.envFieldCard}
									>
										<div className={styles.envFieldHeader}>
											<label className={styles.formLabel}>
												{field.label}
												{field.required ? " *" : ""}
											</label>
											<span className={styles.envType}>{field.type}</span>
										</div>

										{field.type === "boolean" ? (
											<div className={styles.sensitiveRow}>
												<span className={styles.sensitiveLabel}>{field.description}</span>
												<Switch
													checked={Boolean(currentValue)}
													onChange={(event) => handleToolEnvChange(field.envName, event.target.checked)}
													color="primary"
													size="small"
												/>
											</div>
										) : isOAuth2 ? (
											<div className={styles.oauthCard}>
												<div className={styles.oauthCardHeader}>
													<div className={styles.oauthProviderBlock}>
														<span className={styles.oauthProviderName}>Provedor: {oauthStatus?.provider}</span>
														<span className={styles.oauthConnectionState}>
															{oauthStatus?.linked ? "Conta vinculada" : "Conta não vinculada"}
														</span>
													</div>
													{oauthStatus?.linked ? (
														<Button
															variant="outlined"
															color="error"
															size="small"
															onClick={() => handleOAuth2Unlink(field.envName)}
															disabled={oauthAction === "unlink"}
														>
															{oauthAction === "unlink" ? "Desconectando..." : "Desconectar"}
														</Button>
													) : (
														<Button
															variant="contained"
															size="small"
															onClick={() => handleOAuth2Link(field.envName)}
														disabled={oauthAction === "link" || hiring}
													>
														{oauthAction === "link"
															? "Redirecionando..."
															: selectedContract
																? `Vincular conta ${oauthStatus?.provider}`
																: `Contratar e vincular ${oauthStatus?.provider}`}
														</Button>
													)}
												</div>
												{oauthStatus?.linkedAt && (
													<p className={styles.oauthLinkedAt}>
														Vinculada em: {new Date(oauthStatus.linkedAt).toLocaleString()}
													</p>
												)}
											</div>
										) : field.type === "json" ? (
											<TextField
												className={styles.formTextarea}
												variant="outlined"
												size="small"
												fullWidth
												multiline
												minRows={4}
												placeholder={field.description}
												value={typeof currentValue === "string" ? currentValue : JSON.stringify(currentValue ?? {}, null, 2)}
												onChange={(event) => handleToolEnvChange(field.envName, event.target.value)}
											/>
										) : (
											<TextField
												className={styles.formInput}
												variant="outlined"
												size="small"
												fullWidth
												type={
													field.type === "number"
														? "number"
														: field.sensitive
															? "password"
															: "text"
												}
												placeholder={field.placeholder ?? field.description}
												value={currentValue ?? ""}
												onChange={(event) => handleToolEnvChange(field.envName, event.target.value)}
												autoComplete={field.sensitive ? "new-password" : undefined}
											/>
										)}
										{field.description && <p className={styles.envDescription}>{field.description}</p>}
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>

			{/* Footer Actions */}
			<div className={styles.footerActions}>
				{selectedContract ? (
					<>
						<Button
							variant="contained"
							color="primary"
							className={styles.saveBtn}
							disableElevation
							onClick={handleSave}
							disabled={saving || !hasConfigChanges}
							startIcon={
								saving ? (
									<CircularProgress
										size={16}
										color="inherit"
									/>
								) : undefined
							}
						>
							{saving ? "Salvando..." : "Salvar configurações"}
						</Button>
						<Button
							variant="outlined"
							color="error"
							onClick={() => setRemoveDialogOpen(true)}
							disabled={removing}
						>
							Remover ferramenta
						</Button>
					</>
				) : (
					<Button
						variant="contained"
						color="primary"
						className={styles.saveBtn}
						disableElevation
						onClick={handleHire}
						disabled={hiring}
						startIcon={
							hiring ? (
								<CircularProgress
									size={16}
									color="inherit"
								/>
							) : undefined
						}
					>
						{hiring ? "Contratando..." : "Contratar"}
					</Button>
				)}
			</div>

			<Dialog
				open={removeDialogOpen}
				onClose={() => (!removing ? setRemoveDialogOpen(false) : undefined)}
			>
				<DialogTitle>Remover {mcp?.name ?? "ferramenta"}?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Esta ação apaga o contrato e suas credenciais.
						{selectedContract && (selectedContract.linkedMembersCount ?? 0) > 0 && (
							<>
								{" "}
								<strong>
									{selectedContract.linkedMembersCount}{" "}
									{selectedContract.linkedMembersCount === 1
										? "especialista usa esta ferramenta e será desvinculado"
										: "especialistas usam esta ferramenta e serão desvinculados"}
								</strong>
								. Os especialistas continuam existindo, mas perdem o acesso a esta tool.
							</>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setRemoveDialogOpen(false)}
						disabled={removing}
					>
						Cancelar
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={handleConfirmRemove}
						disabled={removing}
						startIcon={
							removing ? (
								<CircularProgress
									size={16}
									color="inherit"
								/>
							) : undefined
						}
					>
						{removing ? "Removendo..." : "Remover"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};
